import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { apiClient } from '../services/api'
import { ui } from '../styles/ui'
import { useAuthStore } from '../services/authStore'
import { useTranslation } from '../contexts/TranslationContext'
import legalService from '../services/legalService'
import { formatDateTime } from '../utils/date'
import { useCurrencyCatalog } from '../contexts/CurrencyCatalogContext'
import SearchableCurrencySelect from '../components/ui/SearchableCurrencySelect'
import ChangePasswordCard from '../components/profile/ChangePasswordCard'

export default function Profile() {
  const navigate = useNavigate()
  const { user, setError, checkAuthStatus } = useAuthStore()
  const { setLang, t } = useTranslation()
  const { currencies: CURRENCY_LIST } = useCurrencyCatalog()
  const [profile, setProfile] = useState({
    name: user?.name || '',
    email: user?.email || '',
    default_currency: user?.default_currency || 'USD',
    default_language: user?.default_language || 'en',
  })
  const [errorMessage, setErrorMessage] = useState('')
  const [languages, setLanguages] = useState([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [successMessage, setSuccessMessage] = useState('')
  const [acceptances, setAcceptances] = useState([])
  const [consents, setConsents] = useState([])
  const [consentLoading, setConsentLoading] = useState(false)
  const [consentMessage, setConsentMessage] = useState('')

  useEffect(() => {
    loadProfile()
    loadLanguages()
    loadLegalData()
  }, [])

  const loadProfile = async () => {
    setLoading(true)
    try {
      const response = await apiClient.get('/profile')
      const userData = response.data.user || {}
      setProfile({
        name: userData.name || '',
        email: userData.email || '',
        default_currency: userData.default_currency || 'USD',
        default_language: userData.default_language || 'en',
      })
    } catch (error) {
      console.error('Failed to load profile:', error)
      setErrorMessage(t('Failed to load profile', 'Failed to load profile'))
    } finally {
      setLoading(false)
    }
  }

  const loadLanguages = async () => {
    try {
      const response = await apiClient.get('/languages')
      if (Array.isArray(response.data)) {
        setLanguages(response.data)
      }
    } catch (error) {
      console.error('Failed to load languages:', error)
    }
  }

  const loadLegalData = async () => {
    try {
      const [acceptanceItems, consentItems] = await Promise.all([
        legalService.getAcceptances(),
        legalService.getConsents(),
      ])
      setAcceptances(Array.isArray(acceptanceItems) ? acceptanceItems : [])
      setConsents(Array.isArray(consentItems) ? consentItems : [])
    } catch (error) {
      console.error('Failed to load legal data:', error)
    }
  }

  const isMarketingConsentGranted = () => {
    const latest = consents.find((item) => item.consentType === 'marketing_updates')
    return latest?.status === 'granted'
  }

  const updateMarketingConsent = async (grant) => {
    setConsentLoading(true)
    setConsentMessage('')
    try {
      if (grant) {
        await legalService.grantConsent('marketing_updates')
        setConsentMessage(t('Consent granted', 'Consent granted'))
      } else {
        await legalService.withdrawConsent('marketing_updates')
        setConsentMessage(t('Consent withdrawn', 'Consent withdrawn'))
      }
      await loadLegalData()
    } catch (error) {
      console.error('Failed to update consent:', error)
      setConsentMessage(t('Failed to update consent', 'Failed to update consent'))
    } finally {
      setConsentLoading(false)
    }
  }

  const handleSave = async () => {
    setSaving(true)
    try {
      const response = await apiClient.put('/profile', {
        name: profile.name,
        default_currency: profile.default_currency,
        default_language: profile.default_language,
      })
      const userData = response.data.user || {}
      setProfile({
        ...profile,
        name: userData.name,
        default_currency: userData.default_currency,
        default_language: userData.default_language,
      })
      localStorage.setItem('user', JSON.stringify(userData))
      checkAuthStatus()

      // ensure translation locale matches selected profile language
      if (userData.default_language) {
        setLang(userData.default_language)
      }

      setSuccessMessage(t('Profile updated successfully', 'Profile updated successfully'))
      setErrorMessage('')
      setError('')
    } catch (error) {
      console.error('Failed to update profile:', error)
      const message = error.response?.data?.message || t('Failed to update profile', 'Failed to update profile')
      setErrorMessage(message)
      setError(message)
    } finally {
      setSaving(false)
    }
  }

  const setField = (field, value) => setProfile(prev => ({ ...prev, [field]: value }))

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-surface-elevated p-4">
        <div className={`${ui.card} ${ui.cardBody} text-center`}>
          <div className={`${ui.spinner} mx-auto mb-3`}></div>
          <div className="text-sm font-medium text-foreground">{t('Loading your profile...', 'Loading your profile...')}</div>
        </div>
      </div>
    )
  }

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <div className={`${ui.card} ${ui.cardBody}`}>
        <h1 className={`${ui.title} mb-4`}>{t('My Profile', 'My Profile')}</h1>

        {successMessage ? (
          <div className="mb-4 rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800">
            {successMessage}
          </div>
        ) : null}
        {errorMessage ? (
          <div className={`mb-4 ${ui.alertError}`}>{errorMessage}</div>
        ) : null}

        <div className="space-y-4">
          <div>
            <label className={ui.label}>{t('Name', 'Name')}</label>
            <input
              type="text"
              value={profile.name}
              onChange={(e) => setField('name', e.target.value)}
              className={`mt-1 ${ui.input}`}
            />
          </div>

          <div>
            <label className={ui.label}>{t('Email (read-only)', 'Email (read-only)')}</label>
            <input
              type="email"
              value={profile.email}
              readOnly
              className="mt-1 w-full rounded-md border border-outline bg-surface-elevated px-3 py-2 text-sm"
            />
          </div>

          <div>
            <label className={ui.label}>{t('Default Currency', 'Default Currency')}</label>
            <SearchableCurrencySelect
              id="profile-default-currency"
              value={profile.default_currency}
              options={CURRENCY_LIST}
              onChange={(value) => setField('default_currency', value)}
              emptyLabel={t('Select currency', 'Select currency')}
              searchPlaceholder={t('Search currency', 'Search currency')}
              searchAriaLabel={t('Search currency', 'Search currency')}
              className="mt-1"
            />
          </div>

          <div>
            <label className={ui.label}>{t('Language', 'Language')}</label>
            <select
              value={profile.default_language}
              onChange={(e) => {
                const nextLang = e.target.value
                setField('default_language', nextLang)
                setLang(nextLang)
              }}
              className={`mt-1 ${ui.select}`}
            >
              {languages.length > 0 ? languages.map((language) => (
                <option key={language.code} value={language.code}>
                  {language.name} ({language.code})
                </option>
              )) : (
                <option value="en">{t('English (en)', 'English (en)')}</option>
              )}
            </select>
          </div>

          <div className={ui.forms.actions}>
            <button
              onClick={handleSave}
              disabled={saving}
              className={ui.buttonPrimary}
            >
              {saving ? t('Saving...', 'Saving...') : t('Save Profile', 'Save Profile')}
            </button>

            <button
              onClick={() => navigate('/')}
              className={ui.buttonGhost}
            >
              {t('Back to Dashboard', 'Back to Dashboard')}
            </button>
          </div>
        </div>
      </div>

      <ChangePasswordCard />

      <div className={`${ui.card} ${ui.cardBody}`}>
        <h2 className="mb-3 text-base font-semibold text-foreground">{t('Storage & Recognition Privacy', 'Storage & Recognition Privacy')}</h2>
        <div className="mb-4 rounded-md border border-blue-200 bg-blue-50 p-3 text-sm text-blue-900">
          <p>
            {t(
              'privacy.storage_notice',
              'Receipts are stored only on your device unless you create an account and enable cloud sync.',
            )}
          </p>
          <p className="mt-2">
            {t(
              'privacy.recognition_notice',
              'For recognition, receipt images and text may be temporarily sent to our backend and configured OCR/AI providers. Anonymous receipt content is not stored after recognition.',
            )}
          </p>
        </div>

        <h2 className="mb-3 text-base font-semibold text-foreground">{t('Legal', 'Legal')}</h2>
        {consentMessage ? (
          <div className="mb-3 rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-700">
            {consentMessage}
          </div>
        ) : null}
        <div className="mb-3 rounded-md border border-outline p-3">
          <p className="text-sm font-medium text-foreground">
            {t('Optional consent: Product updates', 'Optional consent: Product updates')}
          </p>
          <p className="mb-2 text-xs text-muted-foreground">
            {t('You can grant or withdraw this consent anytime.', 'You can grant or withdraw this consent anytime.')}
          </p>
          <p className="mb-2 text-xs text-muted-foreground">
            {t('Current status', 'Current status')}: {isMarketingConsentGranted() ? 'granted' : 'withdrawn'}
          </p>
          <div className="flex gap-2">
            <button
              onClick={() => updateMarketingConsent(true)}
              disabled={consentLoading || isMarketingConsentGranted()}
              className={ui.buttonPrimary}
            >
              {t('Grant', 'Grant')}
            </button>
            <button
              onClick={() => updateMarketingConsent(false)}
              disabled={consentLoading || !isMarketingConsentGranted()}
              className={ui.buttonGhost}
            >
              {t('Withdraw', 'Withdraw')}
            </button>
          </div>
        </div>

        <div className="rounded-md border border-outline p-3">
          <p className="mb-2 text-sm font-medium text-foreground">
            {t('Accepted document versions', 'Accepted document versions')}
          </p>
          {acceptances.length === 0 ? (
            <p className="text-xs text-muted-foreground">{t('No legal acceptances found.', 'No legal acceptances found.')}</p>
          ) : (
            <div className="space-y-2">
              {acceptances.slice(0, 5).map((item) => (
                <div key={item.id} className="rounded border border-divider p-2">
                  <p className="text-xs font-medium text-foreground">{item.documentType}</p>
                  <p className="text-xs text-muted-foreground">{t('Version', 'Version')}: {item.version}</p>
                  <p className="text-xs text-muted-foreground">
                    {t('Accepted at', 'Accepted at')}: {item.acceptedAt ? formatDateTime(item.acceptedAt) : 'n/a'}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

